import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  AppBar,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import MenuIcon from '@mui/icons-material/Menu';
import { useAuth } from '../auth/AuthContext';
import AccountMenu from './AccountMenu';
import AppBreadcrumbs from './AppBreadcrumbs';
import { getMainNavItems } from '../constants/navigation';
import { BOARD_MANUAL_PATH, PATIENTS_PATH } from '../constants/routes';

const DRAWER_WIDTH = 240;
const SIDEBAR_STORAGE_KEY = 'onod-sidebar-open';

const MainLayout: React.FC = () => {
  const theme = useTheme();
  const isNarrowViewport = useMediaQuery(theme.breakpoints.down('md'));
  /** モバイル: 一時ドロワーの開閉 */
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  /** デスクトップ: 常時ドロワーの開閉（localStorage と同期） */
  const [desktopNavOpen, setDesktopNavOpen] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_STORAGE_KEY) !== '0';
    } catch {
      return true;
    }
  });
  const { staffProfileError, staffProfile } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isAdmin = staffProfile?.role === '管理者';
  const navItems = getMainNavItems(isAdmin);

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_STORAGE_KEY, desktopNavOpen ? '1' : '0');
    } catch {
      /* noop */
    }
  }, [desktopNavOpen]);

  const handleDrawerClose = useCallback(() => {
    if (isNarrowViewport) setMobileNavOpen(false);
    else setDesktopNavOpen(false);
  }, [isNarrowViewport]);

  const handleNavItemNavigate = useCallback(
    (path: string | null) => {
      if (!path) return;
      navigate(path);
      if (isNarrowViewport) setMobileNavOpen(false);
    },
    [navigate, isNarrowViewport]
  );

  // ナロー表示でルートが変わったらドロワーを閉じる（戻る・リダイレクト含む）
  useEffect(() => {
    if (isNarrowViewport) setMobileNavOpen(false);
  }, [location.pathname, isNarrowViewport]);

  // ワイド表示に戻したときモバイル用の開状態をリセット
  useEffect(() => {
    if (!isNarrowViewport) setMobileNavOpen(false);
  }, [isNarrowViewport]);

  const isItemSelected = (path: string | null) => {
    if (!path) return false;
    const p = location.pathname;
    if (path === BOARD_MANUAL_PATH) {
      return (
        p === BOARD_MANUAL_PATH ||
        p === '/bulletin' ||
        p === '/bulletin/new' ||
        /^\/bulletin\/\d+$/.test(p) ||
        /^\/bulletin\/\d+\/edit$/.test(p) ||
        p === '/manual' ||
        p === '/manual/new' ||
        p === '/manual/groups' ||
        /^\/manual\/\d+$/.test(p) ||
        /^\/manual\/\d+\/edit$/.test(p)
      );
    }
    if (path === PATIENTS_PATH) {
      // 新規受付は一覧から遷移するため、/patients/new でも「患者一覧」を選択状態にする
      return (
        p === '/patients' ||
        p === '/patients/new' ||
        /^\/patients\/\d+$/.test(p) ||
        /^\/patients\/\d+\/edit$/.test(p)
      );
    }
    if (path === '/settings') {
      return p === '/settings';
    }
    if (path === '/staff') {
      return p === '/staff' || /^\/staff\/\d+\/edit$/.test(p) || p === '/staff/new';
    }
    return p === path;
  };

  const drawerPaperSx = {
    width: DRAWER_WIDTH,
    boxSizing: 'border-box' as const,
    borderRight: 1,
    borderColor: 'divider',
  };

  const drawerOpen = isNarrowViewport ? mobileNavOpen : desktopNavOpen;

  return (
    <Box sx={{ display: 'flex', height: '100dvh', minHeight: 0, overflow: 'hidden' }}>
      <Drawer
        variant={isNarrowViewport ? 'temporary' : 'persistent'}
        open={drawerOpen}
        onClose={handleDrawerClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          flexShrink: 0,
          // persistent 時は Paper がデフォルトで position:fixed のため main と重なる。ドロワー枠に幅を持たせ、Paper を relative にして横並びに乗せる
          ...(!isNarrowViewport && {
            width: desktopNavOpen ? DRAWER_WIDTH : 0,
            overflow: 'hidden',
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
          }),
          '& .MuiDrawer-paper': {
            ...drawerPaperSx,
            ...(!isNarrowViewport && {
              position: 'relative',
              left: 'auto',
              top: 'auto',
              height: '100%',
              minHeight: '100vh',
            }),
          },
        }}
      >
        <Toolbar sx={{ px: 2, minHeight: 56, alignItems: 'center' }}>
          <Box
            component={RouterLink}
            to={PATIENTS_PATH}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              textDecoration: 'none',
              color: 'inherit',
              borderRadius: 1,
              py: 0.5,
              px: 0.5,
              mx: -0.5,
              flexShrink: 0,
            }}
          >
            <Box
              component="img"
              src={`${process.env.PUBLIC_URL}/onod-abokado.png`}
              alt=""
              sx={{ height: 32, width: 'auto', maxWidth: 40, objectFit: 'contain', flexShrink: 0 }}
            />
            <Typography variant="subtitle1" component="span" fontWeight={700} noWrap>
              ONOD
            </Typography>
          </Box>
        </Toolbar>
        <Divider />
        <List sx={{ px: 1, py: 1 }}>
          {navItems.map((item) => (
            <ListItemButton
              key={item.key}
              selected={isItemSelected(item.path)}
              disabled={!item.path}
              onClick={() => handleNavItemNavigate(item.path)}
              sx={{ borderRadius: 1, mb: 0.25 }}
            >
              <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>{item.icon}</ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{ variant: 'body2', noWrap: true }}
              />
            </ListItemButton>
          ))}
        </List>
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          minHeight: 0,
          overflow: 'hidden',
          bgcolor: 'grey.50',
        }}
      >
        <AppBar
          position="static"
          color="inherit"
          elevation={0}
          sx={{
            flexShrink: 0,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Toolbar variant="dense" sx={{ gap: 2, minHeight: 48 }}>
            {isNarrowViewport ? (
              <IconButton
                edge="start"
                color="inherit"
                aria-label="ナビゲーションを開く"
                onClick={() => setMobileNavOpen(true)}
                sx={{ mr: -0.5 }}
              >
                <MenuIcon />
              </IconButton>
            ) : (
              <IconButton
                edge="start"
                color="inherit"
                aria-label={desktopNavOpen ? 'サイドバーを閉じる' : 'サイドバーを開く'}
                onClick={() => setDesktopNavOpen((v) => !v)}
                sx={{ mr: -0.5 }}
              >
                {desktopNavOpen ? <ChevronLeftIcon /> : <MenuIcon />}
              </IconButton>
            )}
            <AppBreadcrumbs />
            <Box sx={{ flexGrow: 1 }} />
            <AccountMenu />
          </Toolbar>
        </AppBar>

        {staffProfileError === 'forbidden' && (
          <Alert severity="warning" sx={{ borderRadius: 0 }}>
            職員として登録されていません。管理者に登録を依頼してください。
          </Alert>
        )}
        {staffProfileError === 'other' && (
          <Alert severity="error" sx={{ borderRadius: 0 }}>
            職員情報の取得に失敗しました。しばらくしてから再度お試しください。
          </Alert>
        )}
        {staffProfile?.is_developer && (
          <Alert severity="info" sx={{ borderRadius: 0 }}>
            開発者モード: 職員管理以外の院内データはサーバー側でマスクされています。
          </Alert>
        )}

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            p: { xs: 2, sm: 3 },
            width: '100%',
            minWidth: 0,
            overflow: 'hidden',
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default MainLayout;
